import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "motion/react";
import KeyboardEventHandler from "@infinium/react-keyboard-event-handler";
import { useMediaQuery } from "react-responsive";
import { RemoveScroll } from "react-remove-scroll";
import { useOverlayContext } from "../../functional-components/OverlayContext";
import useIsMounted from "../../hooks/useIsMounted";
import "./style.scss";

const SelectBoxOverlay = ({ top = 0, width = 400, close, children }) => {
  const isMounted = useIsMounted();
  const isSmall = useMediaQuery({ maxWidth: 767.98 });
  const { setOverlayOpened } = useOverlayContext();
  const SEARCH_BOX_HEIGHT = 64;
  const elmRef = useRef();
  const [modalRoot, setModalRoot] = useState();
  const portalId = "select-box-portal-root";

  useEffect(() => {
    setTimeout(() => {
      if (isMounted()) {
        if (isSmall) setOverlayOpened(true);
        setTimeout(() => {
          if (elmRef.current)
            elmRef.current.scrollIntoView({
              block: "end",
              behavior: "auto"
            });
        }, 100);
      }
    }, 10);
    return () => {
      setOverlayOpened(false);
    };
  }, [isMounted, isSmall, setOverlayOpened]);

  useLayoutEffect(() => {
    let _modalRoot = document.getElementById(portalId);
    if (!_modalRoot) {
      _modalRoot = document.createElement("div");
      _modalRoot.setAttribute("id", portalId);
      document.body.append(_modalRoot);
    }
    setModalRoot(_modalRoot);
  }, []);

  const docClickHandler = useCallback(
    e => {
      if (!elmRef.current?.contains(e.target)) {
        if (!isSmall) close?.();
      }
    },
    [close, isSmall]
  );

  useEffect(() => {
    document.body.addEventListener("click", docClickHandler);
    return () => {
      document.body.removeEventListener("click", docClickHandler);
    };
  }, [docClickHandler]);

  if (!modalRoot) return null;

  return isSmall ? (
    modalRoot ? (
      createPortal(
        <>
          <motion.div
            ref={elmRef}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, transition: { duration: 0.15 } }}
            transition={{ duration: 0.2, delay: 0.15 }}
            className="select-box-overlay"
            style={{
              position: "fixed",
              top: "calc(var(--menu-height) + var(--padding-top))",
              bottom: 0,
              left: 0,
              right: 0
            }}
          >
            {children}
          </motion.div>
          <KeyboardEventHandler
            handleKeys={["esc"]}
            handleFocusableElements
            onKeyEvent={(key, e) => {
              switch (key) {
                case "esc":
                  close();
                  e.preventDefault();
                  break;
                default:
              }
            }}
          />
        </>,
        modalRoot
      )
    ) : null
  ) : (
    <AnimatePresence>
      <RemoveScroll enabled={isSmall}>
        <motion.div
          ref={elmRef}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0, transition: { duration: 0.15 } }}
          transition={{ duration: 0.2, delay: 0.15 }}
          className="select-box-overlay"
          style={{
            position: "absolute",
            top,
            left: 0,
            width,
            maxHeight:
              "calc(100vh - var(--menu-height) - var(--padding-top) - " +
              SEARCH_BOX_HEIGHT +
              "px)"
          }}
        >
          {children}
        </motion.div>
      </RemoveScroll>
    </AnimatePresence>
  );
};

export default SelectBoxOverlay;
